import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useMemo, useEffect, useReducer, useCallback } from 'react';
import axios, { endpoints } from 'src/utils/axios';
import { AuthContext } from './auth-context';
import { setSession, isValidToken, getemployeeno } from './utils';


const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

const STORAGE_KEY = 'accessToken';

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        const en = getemployeeno(accessToken);
        const response = await axios.get(`${endpoints.auth.me}?en=${en}`);
        const user = response.data;
        dispatch({
          type: 'INITIAL',
          payload: {
            user: {
              ...user,
              accessToken,
            },
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const login = useCallback(async (username, password, redirectPath) => {
    try {
      const data = { username, password };
  
      // เรียก API เพื่อเข้าสู่ระบบ
      const response = await axios.post(endpoints.auth.login, data);
      const { token } = response.data;
  
      // ตั้งค่า session ด้วย token
      setSession(token);
  
      // เรียก API `getemployeeno` เพื่อดึง Employee Number
      const en = await getemployeeno(token);
  
      // เรียกข้อมูลผู้ใช้
      const response2 = await axios.get(`${endpoints.auth.me}?en=${en}`);
      const user = response2.data;
  
      // อัปเดต state ผ่าน dispatch
      dispatch({
        type: 'LOGIN',
        payload: {
          user: {
            ...user,
            token,
          },
        },
      });
  
      // เปลี่ยนเส้นทางไปยัง path ที่กำหนด
      navigate(redirectPath);
    } catch (error) {
      console.error('Error during login:', error);
      // คุณอาจเพิ่มการแจ้งเตือนหรือจัดการข้อผิดพลาดเพิ่มเติมที่นี่
    }
  }, [navigate, dispatch]);
  

  const handleLineLogin = useCallback((token,redirectPath = '/dashboard/user/cardbusiness') => {
    setSession(token);
    setTimeout(() => {
    const en = getemployeeno(token);
    axios.get(`${endpoints.auth.me}?en=${en}`).then(response => {
      const user = response.data;
      dispatch({
        type: 'LOGIN',
        payload: {
          user: {
            ...user,
            token,
          },
        },
      });
    });
  }, 500);
    navigate(redirectPath);
  }, [navigate, dispatch]);

  const logout = useCallback(async () => {
    try {
      // ล้าง session (ลบ token ออกจาก localStorage และ axios header)
      setSession(null);
  
      // ลบข้อมูลเพิ่มเติมใน localStorage หรือ cookies
      localStorage.removeItem('accessToken'); 
  
      // รีเซ็ต Context ผ่าน dispatch
      dispatch({
        type: 'LOGOUT',
      });
  
      // เปลี่ยนเส้นทางไปยังหน้าล็อกอินหรือหน้าแรก
      navigate('/login'); // นำผู้ใช้ไปยังหน้าล็อกอิน
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, [dispatch, navigate]);
  

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';
  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      login,
      handleLineLogin,
      logout,
    }),
    [login, handleLineLogin, logout, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
